import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { graviGet, graviSend } from './request'
import type { CreateThreadBody, GraviThread, GraviThreadDetail } from './types'

/**
 * Thread CRUD hooks for the gravitutor proxy. Ported from S&D
 * `api/gravi/useThreads.ts`.
 *
 * TODO(genapi): once `/gravitutor/*` is in the generated OpenAPI types, replace
 * these with the typed `$api` client (`$api.useQuery('get', '/gravitutor/threads')`,
 * `$api.useMutation('post', '/gravitutor/threads')`, etc.) — see `./types.ts`.
 */

const THREADS_KEY = ['gravitutor', 'threads'] as const
const threadDetailKey = (threadId: string) =>
  ['gravitutor', 'threads', threadId] as const

export function useThreadsList() {
  return useQuery({
    queryKey: THREADS_KEY,
    queryFn: ({ signal }) =>
      graviGet<GraviThread[]>('/gravitutor/threads', signal),
  })
}

export function useThreadDetail(threadId: string | undefined) {
  return useQuery({
    queryKey: threadDetailKey(threadId ?? ''),
    queryFn: ({ signal }) =>
      graviGet<GraviThreadDetail>(`/gravitutor/threads/${threadId}`, signal),
    enabled: Boolean(threadId),
  })
}

export function useCreateThread() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body?: CreateThreadBody) =>
      graviSend<GraviThread>('POST', '/gravitutor/threads', body ?? {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: THREADS_KEY })
    },
  })
}

export function useDeleteThread() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (threadId: string) =>
      graviSend<void>('DELETE', `/gravitutor/threads/${threadId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: THREADS_KEY })
    },
  })
}
